import { Tooltip } from './TooltipNew';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { TooltipContainer } from './Tooltip';
import { TextareaAutosize } from '@mui/material';
import { ShortcutLegendIcon } from './ShortcutLegendIcon';
import { darkModeColors } from '../../util';
import clsx from 'clsx';
import { hide, offset, shift } from '@floating-ui/react';
import { useAssessmentEditableField } from '../hooks/useAssessmentEditableField';

const containerVariants: Variants = {
  inUse: {
    scale: 1.02,
    borderRadius: 12,
    padding: '8px 16px',
    backgroundColor: darkModeColors.almostBlack,
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2)',
  },
  standby: {
    scale: 1,
    borderRadius: 12,
    padding: '8px 16px',
    backgroundColor: darkModeColors.almostBlack,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.2), 0 2px 4px -2px rgb(0 0 0 / 0.2)',
  },
  nonEditable: {
    scale: 1,
    padding: 0,
    borderRadius: 0,
    backgroundColor: `${darkModeColors.almostBlack}1A`,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0), 0 2px 4px -2px rgb(0 0 0 / 0)',
  },
};
const tooltipVariants: Variants = {
  shown: { y: 0, opacity: 1 },
  hidden: { y: -10, opacity: 0 },
};

export interface AssessmentEditableFieldProps {
  readOnlyContent: React.ReactNode;
  textAreaClassName?: string;
  useAssessmentEditableFieldReturn: ReturnType<typeof useAssessmentEditableField>;
}

export function AssessmentEditableField({
  readOnlyContent,
  textAreaClassName,
  useAssessmentEditableFieldReturn,
}: AssessmentEditableFieldProps) {
  const {
    isEditing,
    enterEditMode,
    exitEditMode,
    editableContent,
    editAssessment,
    id,
    placeholder,
    setEditableContent,
  } = useAssessmentEditableFieldReturn;

  return (
    <motion.div
      variants={containerVariants}
      transition={{ duration: 0.2 }}
      whileHover={editAssessment?.enabled ? 'inUse' : undefined}
      onClick={editAssessment?.enabled ? enterEditMode : undefined}
      initial={editAssessment?.enabled ? (isEditing ? 'inUse' : 'standby') : 'nonEditable'}
      animate={editAssessment?.enabled ? (isEditing ? 'inUse' : 'standby') : 'nonEditable'}
      className={clsx(
        editAssessment?.enabled && 'cursor-pointer border border-darkMode-gray-light/10',
        'reactSummary-container overflow-hidden'
      )}
    >
      <Tooltip
        placement="bottom"
        floatingClassName="!z-[1]"
        middleware={[offset(4), hide(), shift({ crossAxis: true, padding: { bottom: 24 } })]}
        floatingElement={
          editAssessment?.enabled &&
          !editAssessment?.hideTooltips &&
          (editAssessment?.fieldInEditID === id || editAssessment?.fieldInEditID === null) && (
            <AnimatePresence mode="wait">
              {isEditing ? (
                <TooltipContainer
                  key="save-draft"
                  motionProps={{
                    exit: 'hidden',
                    animate: 'shown',
                    initial: 'hidden',
                    variants: tooltipVariants,
                    transition: { duration: 0.2 },
                  }}
                  onClick={exitEditMode}
                  className="text-sm !bg-darkMode-almostBlack cursor-pointer"
                >
                  Save <ShortcutLegendIcon shortcutCharacter="esc / click-away" />
                </TooltipContainer>
              ) : (
                <TooltipContainer
                  key="edit"
                  motionProps={{
                    exit: 'hidden',
                    animate: 'shown',
                    initial: 'hidden',
                    variants: tooltipVariants,
                    transition: { duration: 0.2 },
                  }}
                  onClick={enterEditMode}
                  className="text-sm !bg-darkMode-almostBlack cursor-pointer"
                >
                  Click To Edit
                </TooltipContainer>
              )}
            </AnimatePresence>
          )
        }
      >
        {isEditing ? (
          <TextareaAutosize
            autoFocus
            onBlur={exitEditMode}
            value={editableContent}
            placeholder={placeholder}
            onChange={(e) => setEditableContent(e.target.value)}
            className={clsx(
              'w-full flex bg-transparent placeholder:!text-white/20 text-white resize-none caret-palette-300 selection:bg-palette-300/20',
              textAreaClassName
            )}
          />
        ) : (
          readOnlyContent
        )}
      </Tooltip>
    </motion.div>
  );
}
